import { useState, useEffect } from 'react'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import BlockManager from './BlockManager'
import MobileSidebar from './MobileSidebar'
import Header from './Header'
import PageBuilderHeader from './PageBuilderHeader'
import usePageBuilderStore from '../stores/pageBuilderStore'
import { useAuthStore } from '../stores/authStore'
import { MailReviewCacheGuard } from './MailReviewAccess'
import {
  canAccessMailReview,
  isOwnerOnly,
} from '../features/mailReview/access'

function BuilderSidebar({ entityId, entityType, blockField }) {
  const viewMode = usePageBuilderStore((state) => state.viewMode)
  return (
    <BlockManager
      entityId={entityId}
      entityType={entityType}
      blockField={blockField}
      viewMode={viewMode}
    />
  )
}

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const content = children || <Outlet />
  const { user, freshAuthVerified, refreshUser, logout } = useAuthStore()

  useEffect(() => {
    void refreshUser()
  }, [refreshUser])

  if (freshAuthVerified !== true) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <MailReviewCacheGuard />
        <p className="text-sm text-gray-500">Verifying session...</p>
      </div>
    )
  }

  const ownerOnly = isOwnerOnly(user)
  const ownerCanAccessMailReview = canAccessMailReview({
    user,
    freshAuthVerified,
  })

  if (ownerOnly && !ownerCanAccessMailReview) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MailReviewCacheGuard />
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          <Sidebar />
        </div>
        <div className="lg:pl-64">
          <Header
            onMenuClick={() => setSidebarOpen(true)}
            showLanguageSwitcher={false}
            showMenuButton={false}
          />
          <main className="px-4 py-10 text-center text-sm text-gray-500">
            <p>Mail Review is unavailable for this account.</p>
            <button
              type="button"
              className="mt-5 rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm hover:bg-gray-50 lg:hidden"
              aria-label="Sign out from Mail Review"
              onClick={logout}
            >
              Sign out
            </button>
          </main>
        </div>
      </div>
    )
  }

  if (ownerOnly && location.pathname !== '/mail-review') {
    return (
      <>
        <MailReviewCacheGuard />
        <Navigate to="/mail-review" replace />
      </>
    )
  }

  const isPageBuilder =
    location.pathname.startsWith('/pages/') &&
    location.pathname.includes('/builder')
  const isPostBuilder =
    location.pathname.startsWith('/posts/') &&
    !location.pathname.endsWith('/posts')
  const isProductBuilder =
    location.pathname.startsWith('/products/') &&
    !location.pathname.endsWith('/products')
  const isTemplateBuilder =
    location.pathname.startsWith('/templates/') &&
    !location.pathname.endsWith('/templates')

  const builderMode = isPageBuilder
    ? { entityType: 'page', blockField: 'page_id' }
    : isPostBuilder
      ? { entityType: 'post', blockField: 'post_id' }
      : isProductBuilder
        ? { entityType: 'product', blockField: 'product_id' }
        : isTemplateBuilder
          ? { entityType: 'template', blockField: 'content_template_id' }
          : null

  const builderEntityId = builderMode ? location.pathname.split('/')[2] : null

  if (builderMode) {
    return (
      <div className="h-screen flex flex-col bg-gray-50">
        <MailReviewCacheGuard />
        <MobileSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isPageBuilder={true}
          pageId={builderEntityId}
          entityType={builderMode.entityType}
          blockField={builderMode.blockField}
        />

        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          <Sidebar>
            <BuilderSidebar
              entityId={builderEntityId}
              entityType={builderMode.entityType}
              blockField={builderMode.blockField}
            />
          </Sidebar>
        </div>

        <div className="lg:pl-64 flex-1 flex flex-col overflow-hidden">
          <PageBuilderHeader
            onMenuClick={() => setSidebarOpen(true)}
            pageId={builderEntityId}
          />
          <div className="flex-1 overflow-hidden">{content}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MailReviewCacheGuard />
      <MobileSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isPageBuilder={false}
        pageId={null}
      />

      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <Sidebar />
      </div>

      <div className="lg:pl-64">
        <Header
          onMenuClick={() => setSidebarOpen(true)}
          showLanguageSwitcher={!ownerOnly}
        />

        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {content}
          </div>
        </main>
      </div>
    </div>
  )
}
